/**
 * Invite & affiliate — the "推广分佣" page. Mocks an invite link + earnings
 * table. All numbers are demo data persisted in localStorage.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { SpotlightCard } from '@/components/SpotlightCard';
import { useAppStore } from '@/lib/store';

const MOCK_EARNINGS = [
  { name: 'Alex L.', plan: 'VIP 年', amount: 30, date: '2026-05-18' },
  { name: 'Mia Z.',  plan: 'VIP 季', amount: 12, date: '2026-05-12' },
  { name: '小张',    plan: 'VIP 月', amount: 6,  date: '2026-05-09' },
];

export function InvitePage(): JSX.Element {
  const user = useAppStore((s) => s.user);
  const [copied, setCopied] = useState(false);

  const inviteCode = (user.email.split('@')[0] ?? 'you').toUpperCase().slice(0, 8);
  const inviteUrl = `https://speedrun.run/i/${inviteCode}`;

  function copy(): void {
    void navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  const total = MOCK_EARNINGS.reduce((a, b) => a + b.amount, 0);

  return (
    <div className="space-y-10">
      <Reveal>
        <SectionHeading
          eyebrow="Affiliate · 推广分佣"
          title={<>每拉一个朋友升级， <span className="text-gradient-warm">分你 ¥6 ~ ¥30</span></>}
          subtitle="推荐链接、专属海报、佣金明细一应俱全。所有 VIP 转化收益你拿 1/10。"
          align="left"
        />
      </Reveal>

      <Reveal delay={0.05}>
        <SpotlightCard spotlightColor="rgba(0, 240, 255, 0.28)">
          <div className="grid gap-6 md:grid-cols-[1fr,200px] items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-honey-300 font-arcade mb-3">
                你的专属邀请链接
              </div>
              <div className="flex gap-2 flex-wrap">
                <input
                  readOnly
                  value={inviteUrl}
                  className="flex-1 min-w-0 rounded-2xl bg-ink-800/60 border border-honey-400/30 px-4 py-2.5 text-cream-100 font-mono text-sm focus:outline-none focus:border-honey-400/80"
                />
                <GlassButton onClick={copy} variant="solid" size="md">
                  {copied ? '已复制 ✓' : '复制链接'}
                </GlassButton>
              </div>
              <div className="mt-3 text-xs text-cream-400">
                邀请码 <span className="font-mono text-honey-300">{inviteCode}</span> · 朋友首次升级 VIP，你立即到账
              </div>
            </div>
            <PosterMock code={inviteCode} />
          </div>
        </SpotlightCard>
      </Reveal>

      {/* Earnings stats */}
      <Reveal>
        <div className="grid gap-4 md:grid-cols-3">
          <Stat label="累计佣金" value={`¥${total}`} tone="lime" emoji="💰" />
          <Stat label="成功转化" value={`${MOCK_EARNINGS.length} 人`} tone="cyan" emoji="🙌" />
          <Stat label="待结算" value="¥0" tone="cream" emoji="🧾" />
        </div>
      </Reveal>

      {/* Earnings table */}
      <Reveal>
        <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
            <div className="font-display font-bold text-white">最近佣金</div>
            <Link to="#" className="text-xs text-honey-300 hover:text-honey-200">查看全部 →</Link>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {MOCK_EARNINGS.map((e) => (
              <motion.div
                key={e.date}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="px-5 py-4 flex items-center gap-4"
              >
                <div className="h-9 w-9 rounded-full bg-honey-400/15 border border-honey-400/30 grid place-items-center text-honey-300 font-bold text-sm">
                  {e.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium">{e.name}</div>
                  <div className="text-xs text-cream-400">{e.plan} · {e.date}</div>
                </div>
                <div className="text-mint-400 font-display font-extrabold">+¥{e.amount}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* How it works */}
      <Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { step: '01', emoji: '📤', title: '分享链接', desc: '把上面的专属链接发给朋友 / 群 / 朋友圈' },
            { step: '02', emoji: '✨', title: '朋友升级', desc: '朋友通过链接注册并开通任意 VIP（月 / 季 / 年）' },
            { step: '03', emoji: '💸', title: '佣金到账', desc: '次日 00:00 自动到账，可提现到微信钱包' },
          ].map((s) => (
            <div key={s.step} className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5">
              <div className="font-arcade text-honey-300 text-xs">{s.step}</div>
              <div className="text-3xl mt-1">{s.emoji}</div>
              <div className="mt-2 font-display font-bold text-white">{s.title}</div>
              <div className="text-xs text-cream-400 mt-1 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal>
        <div className="rounded-3xl border border-honey-400/20 bg-gradient-to-br from-honey-400/[0.04] to-ember-500/[0.04] p-6 text-center">
          <div className="text-xs text-cream-400 font-arcade tracking-widest">COMMISSION TABLE</div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <Tier label="VIP 月卡" amt="¥6" />
            <Tier label="VIP 季卡" amt="¥12" />
            <Tier label="VIP 年卡" amt="¥30" />
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function Stat({ label, value, tone, emoji }: { label: string; value: string; tone: 'lime' | 'cyan' | 'cream'; emoji: string }): JSX.Element {
  const t = tone === 'lime' ? 'text-mint-400' : tone === 'cyan' ? 'text-honey-400' : 'text-cream-200';
  return (
    <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="flex items-center justify-between">
        <span className="text-xl">{emoji}</span>
        <span className={`font-display text-3xl font-extrabold ${t} text-neon`}>{value}</span>
      </div>
      <div className="mt-3 text-xs text-cream-400">{label}</div>
    </div>
  );
}

function Tier({ label, amt }: { label: string; amt: string }): JSX.Element {
  return (
    <div className="rounded-2xl bg-ink-900/40 border border-honey-400/20 px-4 py-3">
      <div className="text-xs text-cream-400">{label}</div>
      <div className="mt-1 font-display text-2xl font-extrabold text-honey-300">{amt}</div>
    </div>
  );
}

function PosterMock({ code }: { code: string }): JSX.Element {
  return (
    <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-ink-800 to-ink-900 border border-honey-400/30 p-3 flex flex-col gap-2">
      <div className="text-[8px] font-arcade text-honey-300 tracking-widest">SCAN ME</div>
      <div className="flex-1 bg-white rounded-lg grid place-items-center">
        <div className="grid grid-cols-8 gap-px p-2">
          {Array.from({ length: 64 }, (_, i) => (
            <span key={i} className={`h-1.5 w-1.5 ${Math.random() > 0.45 ? 'bg-ink-950' : 'bg-white'}`} />
          ))}
        </div>
      </div>
      <div className="text-[8px] text-cream-400 font-mono text-center truncate">/{code}</div>
    </div>
  );
}
